import '@mdvip/shared/styles/globals.css';

import { initNativeKeyboard } from '@mdvip/shared/lib/native-keyboard';
import { configureTelemetry } from '@mdvip/shared/lib/telemetry';
import { initializeWebVitals } from '@mdvip/shared/lib/web-vitals';
import { useAuthStore } from '@mdvip/shared/stores/authStore';
import { ErrorBoundary } from '@mdvip/shared/system/ErrorBoundary';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { ConfigProvider } from './contexts/ConfigContext';
import i18n from './lib/i18n';

// Configure shared packages for providers portal
configureTelemetry({ cloudRole: 'providers-frontend', portalType: 'providers' });

// Put the iOS keyboard in Native resize mode so modals can track it via dvh (no-op on web).
void initNativeKeyboard();

// Initialize web vitals (doesn't depend on config)
initializeWebVitals();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// Clear all cached data on session end so no PHI is readable by a subsequent user.
// Fires on any user → null transition: explicit logout, cross-tab logout, future expiry.
useAuthStore.subscribe((state, prevState) => {
  if (state.user === null && prevState.user !== null) queryClient.clear();
});

function Root() {
  return (
    <React.StrictMode>
      <ErrorBoundary>
        <I18nextProvider i18n={i18n}>
          <ConfigProvider>
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <App />
              </AuthProvider>
            </QueryClientProvider>
          </ConfigProvider>
        </I18nextProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
}

const rootElement = document.getElementById('root');
if (rootElement) {
  ReactDOM.createRoot(rootElement).render(<Root />);
}
